import { useState } from "react";
import { Link } from "react-router-dom";

import { IconFolder, IconFolderOpen, IconPlus, IconTriangle } from "@/components/Icons";
import { Button, Card, Empty, MenuItem, MenuLabel, PageHead, Popover } from "@/components/kit";
import type { CompetitionTree, TreeNode } from "@/lib/api/types";

import styles from "./Tree.module.css";

/**
 * A sport's competitions, as an outline.
 *
 * The tree has always allowed any shape and any depth — which is what lets cricket
 * hold a tour, a league season, a World Cup and a one-off match at once, and what
 * let it hold nonsense just as easily. This is where the rules a sport declared
 * about its own levels finally do something: "add inside" offers only what that
 * level is allowed to hold, so a wrong shape stops being as easy to build as a right
 * one.
 *
 * Three earlier attempts were all row lists, and all made the same mistake: they
 * printed the level on every line. As a pill, then as an eyebrow — boxing's four
 * fight cards said "FIGHT CARD" four times, in the loudest type on the screen,
 * stating the one thing every row had in common.
 *
 * An outline says it instead. A branch carries a folder and a twist; a leaf carries a
 * dot in its level's colour; the elbow connectors say what contains what, so nothing
 * has to be labelled. An expanded branch and everything under it sit on one tinted
 * block, which is what marks out the part you have opened.
 *
 * Dates are shown, which no earlier version did though they were on the wire the
 * whole time. When a competition runs is the second thing anyone wants to know.
 */
export function Tree({ sport, tree }: { sport: string; tree: CompetitionTree }) {
  const total = countNodes(tree.roots);

  return (
    <>
      <PageHead
        title="Competitions"
        note={
          total === 0
            ? "What this sport groups its fixtures under"
            : `${total} ${total === 1 ? "competition" : "competitions"}, outermost first`
        }
        actions={
          <AddInside sport={sport} label="Add" options={tree.canStart} parent={null} strong />
        }
      />

      {!tree.guided && total > 0 && (
        <Card>
          <p className={styles.notice}>
            This sport has not said what its levels may hold, so anything may sit anywhere and
            fixtures may hang from any of them. Set it in{" "}
            <Link to={`/${sport}/setup`}>setup</Link>, under what it keeps, and this screen will
            start offering only what fits.
          </p>
        </Card>
      )}

      {tree.roots.length === 0 ? (
        <Empty
          title="No competitions yet"
          note="A tournament, a season, a meeting, a fight card — whatever this sport groups its fixtures under. Fixtures hang from the levels that allow them."
          action={
            <AddInside
              sport={sport}
              label="Add the first"
              options={tree.canStart}
              parent={null}
              strong
            />
          }
        />
      ) : (
        <div className={styles.cards}>
          {tree.roots.map((root) => (
            <Node
              key={root.id}
              sport={sport}
              node={root}
              tree={tree}
              depth={0}
              lineage={[]}
              isLast
            />
          ))}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ colour -- */

/**
 * A colour per level, for the dot on a leaf.
 *
 * Derived from the level's code, so every stage is the same colour as every other
 * stage and the eye can group them without reading a word. Hand-picked rather than
 * generated: these have to be told apart at 8px, which rules out anything subtle.
 */
const DOTS = ["#e0489a", "#d92d20", "#2563eb", "#12b76a", "#f79009", "#7c3aed", "#0e9384"];

function dotColour(type: string): string {
  let h = 0;
  for (let i = 0; i < type.length; i += 1) h = (h * 31 + type.charCodeAt(i)) | 0;
  return DOTS[Math.abs(h) % DOTS.length];
}

/* ------------------------------------------------------------------ dates -- */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Read an ISO date by hand rather than with `Date`, which reads a bare
 *  `2026-03-20` as UTC midnight and renders the day before it anywhere west of
 *  Greenwich. */
function readDate(iso: string): { day: number; month: number; year: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) };
}

/**
 * When a competition runs, as short as it can be said.
 *
 * A range inside one year names the year once — "20 Mar – 30 May 2026", not
 * "20 Mar 2026 – 30 May 2026". A single date is just itself. Nothing is the empty
 * string, and the caller drops the fact rather than printing a dash.
 */
export function whenLabel(start?: string | null, end?: string | null): string {
  const from = start ? readDate(start) : null;
  const to = end ? readDate(end) : null;
  const one = (d: { day: number; month: number; year: number }, withYear = true) =>
    `${d.day} ${MONTHS[d.month]}${withYear ? ` ${d.year}` : ""}`;

  if (from && to) {
    if (from.year === to.year && from.month === to.month && from.day === to.day) return one(from);
    if (from.year === to.year) return `${one(from, false)} – ${one(to)}`;
    return `${one(from)} – ${one(to)}`;
  }
  if (from) return one(from);
  if (to) return `until ${one(to)}`;
  return "";
}

/* -------------------------------------------------------------------- row -- */

function Node({
  sport,
  node,
  tree,
  depth,
  lineage,
  isLast,
}: {
  sport: string;
  node: TreeNode;
  tree: CompetitionTree;
  /** How far in. Decides the indent, and which rows are their own card. */
  depth: number;
  /**
   * One flag per *grandparent and above*: does that ancestor have more children after
   * the branch we came down through? A rail is drawn where it does, so a vertical line
   * continues past a row only when there is really something below it to reach.
   *
   * Deliberately not the same thing as `depth`, and deriving one from the other is the
   * bug this comment exists to prevent: it was `lineage.length`, so making a root pass
   * an empty lineage — correct, since nothing joins one root to the next — also told
   * every child it was a root. Every row became its own card and lost its indent.
   */
  lineage: boolean[];
  isLast: boolean;
}) {
  const branch = node.children.length > 0;
  const [open, setOpen] = useState(branch);
  const when = whenLabel(node.startDate, node.endDate);
  const holdable = holdableIn(tree, node);
  // At the top the count that means anything is the whole subtree's: a league holds
  // no fixtures and neither do its seasons.
  const fixtures = depth === 0 ? countFixtures(node) : node.fixtureCount;

  const row = (
    <div className={styles.row}>
      {lineage.map((continues, i) => (
        <span
          key={i}
          className={continues ? `${styles.guide} ${styles.guideLine}` : styles.guide}
          aria-hidden
        />
      ))}
      {depth > 0 && (
        <span
          className={isLast ? `${styles.elbow} ${styles.elbowLast}` : styles.elbow}
          aria-hidden
        />
      )}

      <button
        type="button"
        className={[
          styles.twist,
          open ? styles.twistOpen : "",
          branch ? "" : styles.twistNone,
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label={open ? `Collapse ${node.name}` : `Expand ${node.name}`}
        aria-expanded={branch ? open : undefined}
        disabled={!branch}
        onClick={() => setOpen(!open)}
      >
        <IconTriangle size={11} />
      </button>

      {/* A folder for something that contains things, a dot for something that does
          not. The shape carries what a repeated level label was carrying badly. */}
      {branch ? (
        <span className={styles.folder}>
          {open ? <IconFolderOpen size={15} /> : <IconFolder size={15} />}
        </span>
      ) : (
        <span className={styles.dot} style={{ background: dotColour(node.type) }} aria-hidden />
      )}

      <Link
        to={node.external ? `/events/${node.code}` : editHref(sport, node)}
        className={branch ? styles.name : `${styles.name} ${styles.leafName}`}
      >
        {node.name}
      </Link>

      {/* What kind of thing it is, after its name and quiet.
          It had been dropped entirely on the reasoning that the folder icon and the
          connectors said enough — but they say *that it contains things*, not what it
          is, and "Premier T20 League" could as easily be a series as a league. Two
          earlier versions had the opposite fault: a pill or an uppercase eyebrow, the
          loudest thing on the screen, repeated identically down every row. Quiet and
          after the name is the answer to both. */}
      <span className={styles.level}>{node.label}</span>

      <span className={styles.meta}>
        {when && <span className={styles.when}>{when}</span>}
        {node.external && <span className={styles.tag}>belongs to no sport</span>}
        {node.formatCode && <span className={styles.tag}>{node.formatCode}</span>}
        {fixtures > 0 && (
          <Link to={`/${sport}/fixtures`} className={styles.count}>
            {fixtures} fixture{fixtures === 1 ? "" : "s"}
          </Link>
        )}
        {/* Buttons, on the row under the pointer. Kept in the layout at all times so
            nothing shifts as the pointer moves down the outline, revealed on
            `:focus-within` for a keyboard, and always shown where there is no pointer
            to hover with. A Games gets none of them: it belongs to no sport, so
            nothing on this screen may add to it. */}
        {!node.external && (
          <span className={styles.actions}>
            {node.canHoldFixtures && (
              <Link
                to={`/${sport}/fixtures/new`}
                className={styles.button}
                title="Fixtures may hang from this level"
              >
                <IconPlus size={12} />
                Fixture
              </Link>
            )}
            {holdable.length > 0 && (
              <AddInside sport={sport} label="Inside" options={holdable} parent={node} />
            )}
          </span>
        )}
      </span>
    </div>
  );

  const children =
    open && branch
      ? node.children.map((child, i) => (
          <Node
            key={child.id}
            sport={sport}
            node={child}
            tree={tree}
            depth={depth + 1}
            // A root passes no rail: each is the top of its own card and no line joins
            // one to the next, so `!isLast` here drew a vertical line down the left of
            // every subtree that ran past the last child and stopped in mid-air.
            lineage={depth === 0 ? [] : [...lineage, !isLast]}
            isLast={i === node.children.length - 1}
          />
        ))
      : null;

  // Each top-level folder is its own card. One panel around all of them made four
  // separate competitions read as one list of twelve rows.
  if (depth === 0) {
    return (
      <div className={styles.card}>
        {row}
        {children && <div className={styles.inside}>{children}</div>}
      </div>
    );
  }

  return (
    <>
      {row}
      {children}
    </>
  );
}

/* ------------------------------------------------------------------ parts -- */

/** Every node's own editor. The name was not a link at all before, so the one
 *  screen that shows the shape offered no way to change any of it. */
function editHref(sport: string, node: TreeNode): string {
  return `/${sport}/masters/${node.type}/${encodeURIComponent(node.code)}/edit`;
}

function countNodes(nodes: TreeNode[]): number {
  return nodes.reduce((n, node) => n + 1 + countNodes(node.children), 0);
}

/** Fixtures at this node and everywhere below it. A league holds none itself and
 *  neither do its seasons — the only count that means anything on a card is the
 *  whole subtree's. */
function countFixtures(node: TreeNode): number {
  return node.fixtureCount + node.children.reduce((n, child) => n + countFixtures(child), 0);
}

function holdableIn(tree: CompetitionTree, node: TreeNode): { code: string; label: string }[] {
  return node.canHold
    .map((code) => labelOf(tree, code))
    .filter((o): o is { code: string; label: string } => o !== null);
}

function labelOf(tree: CompetitionTree, code: string): { code: string; label: string } | null {
  const started = tree.canStart.find((o) => o.code === code);
  if (started) return started;
  // A level that cannot stand alone is not in canStart, so its label has to come
  // from wherever it appears in the tree — and if it appears nowhere yet, its own
  // code is the honest fallback.
  const found = find(tree.roots, code);
  return found ? { code, label: found.label } : { code, label: code };
}

function find(nodes: TreeNode[], type: string): TreeNode | null {
  for (const node of nodes) {
    if (node.type === type) return node;
    const inside = find(node.children, type);
    if (inside) return inside;
  }
  return null;
}

/** What a level is allowed to contain, as a menu. The options come from the sport's
 * own rules, so this is the guidance rather than a decoration on it. */
function AddInside({
  sport,
  label,
  options,
  parent,
  strong = false,
}: {
  sport: string;
  label: string;
  options: { code: string; label: string }[];
  parent: TreeNode | null;
  strong?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (options.length === 0) return null;

  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      align="right"
      trigger={
        <Button
          tone={strong ? "primary" : "default"}
          className={strong ? styles.menuTrigger : `${styles.smallButton} ${styles.menuTrigger}`}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <IconPlus size={strong ? 15 : 12} />
          {label}
        </Button>
      }
    >
      <MenuLabel>{parent === null ? "Start a" : `Inside ${parent.name}`}</MenuLabel>
      {options.map((option) => (
        <MenuItem
          key={option.code}
          label={option.label}
          onClick={() => {
            // The record form is the one place a node is authored, whichever level
            // it is — the tree only decides what may be offered here.
            const parentPart = parent ? `?parent=${encodeURIComponent(parent.code)}` : "";
            window.location.href = `/${sport}/masters/${option.code}/new${parentPart}`;
          }}
        />
      ))}
    </Popover>
  );
}
